import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2 } from "lucide-react";
import { Logo } from "@/components/site/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Answers() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading");
  const [answers, setAnswers] = useState({});
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${API}/answers/${token}`)
      .then((res) => {
        const qs = Array.isArray(res.data?.questions) ? res.data.questions : [];
        setData({ ...res.data, questions: qs });
        setAnswers(Object.fromEntries(qs.map((q) => [q.id, q.answer || ""])));
        setState("ready");
      })
      .catch(() => setState("invalid"));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setState("saving");
    setError("");
    try {
      await axios.post(`${API}/answers/${token}`, { answers });
      setState("done");
    } catch (err) {
      setError(typeof err?.response?.data?.detail === "string" ? err.response.data.detail : "Couldn't save. Please try again.");
      setState("ready");
    }
  };

  return (
    <div className="min-h-screen bg-[var(--ink)] px-5 py-8 sm:px-10" data-testid="answers-page">
      <div className="mx-auto max-w-2xl">
        <Logo />
        {state === "loading" && <Loader2 className="mx-auto mt-24 h-6 w-6 animate-spin text-[var(--teal)]" />}
        {state === "invalid" && <p className="mt-24 text-lg text-slate-300" data-testid="answers-invalid">This link isn't valid. Please reply to our email instead.</p>}
        {state === "done" && (
          <div className="mt-24" data-testid="answers-done">
            <CheckCircle2 className="h-10 w-10 text-[var(--teal)]" />
            <h1 className="font-serif-d mt-6 text-5xl text-white">Thank you. <span className="italic text-[var(--teal)]">Got it.</span></h1>
            <p className="mt-4 text-slate-300">We'll use your numbers in your bullets. You can reopen this link to change an answer.</p>
          </div>
        )}
        {(state === "ready" || state === "saving") && data && (
          <form onSubmit={submit} className="mt-16" data-testid="answers-form">
            <h1 className="font-serif-d text-5xl leading-[0.95] text-white sm:text-6xl">Hi {data.name}, <span className="italic text-[var(--teal)]">a few numbers.</span></h1>
            <p className="mt-5 text-slate-300">We only use real numbers. Rough estimates are fine; just say if it's approximate.</p>
            <ol className="mt-10 space-y-6">
              {data.questions.map((q, i) => (
                <li key={q.id}>
                  <label className="block text-base text-white" htmlFor={`a-${q.id}`}><span className="font-mono-d mr-2 text-[var(--teal)]">{String(i + 1).padStart(2, "0")}</span>{q.text}</label>
                  <textarea id={`a-${q.id}`} rows={2} value={answers[q.id] || ""} data-testid={`answer-input-${i}`}
                    onChange={(e) => setAnswers({ ...answers, [q.id]: e.target.value })}
                    className="mt-2 w-full rounded-xl border border-white/15 bg-[var(--navy)] px-4 py-3 text-white focus:border-[var(--teal)] focus:outline-none" />
                </li>
              ))}
            </ol>
            {error && <p className="mt-4 text-sm text-[var(--amber)]" role="alert">{error}</p>}
            <button disabled={state === "saving"} data-testid="answers-submit-button"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-[var(--teal)] px-7 py-4 font-semibold text-[var(--ink)] disabled:opacity-70">
              {state === "saving" && <Loader2 className="h-4 w-4 animate-spin" />} Send answers
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
